import React, { useEffect } from 'react';
import styled from 'styled-components';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { DeviceType, ModalType } from '../../shared/enum/EnumRepository';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useTextViewStore } from '../../shared/store/TextViewStore';
import { useDeviceStore } from '../../shared/store/deviceStore';
import {
  useTextTagActions,
  useTextTagState,
} from '../../shared/hooks/useApi/useTextTag';
import HintSmallWrapper from '../../shared/component/headless/button/HintSmallWrapper';
import { getHintMessage } from '../../shared/constants/hintMessages';
import createIcon from '../../asset/icon/create.png';
import { TextTagData } from '../../shared/dto/EntityRepository';
import editIcon from '../../asset/icon/edit.png';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { useUnsavedChanges } from '../../shared/hooks/useUnsavedChanges';
import { usePageOwnerIdentityStore } from '../../shared/store/pageOwnerIdentityStore';
import { canShowTextTagList } from '../../shared/util/visibility/textTagVisibility';
import { useAuth } from '../../shared/hooks/useApi/useAuth';

const TextTagStation: React.FC = () => {
  const { isEditMode } = useEditMode();
  const { isOwner } = useAuth();
  const { setStandardModal } = useModalStore();
  const activeText = useTextViewStore((s) => s.activeText);
  const { deviceType } = useDeviceStore();
  const {
    tags,
    selectedTags,
    untaggedSelected,
    listOwnerAui,
    status,
  } = useTextTagState();
  const {
    getTextTagList,
    addSelectedTag,
    removeSelectedTag,
    selectUntagged,
    clearUntagged,
  } = useTextTagActions();
  const pageOwner = usePageOwnerIdentityStore((s) => s.pageOwner);
  const { requestExitEditMode } = useUnsavedChanges();
  const canShowTags = canShowTextTagList({
    pageOwnerAui: pageOwner?.aui,
    listOwnerAui,
    status,
  });
  const visibleTags = canShowTags ? tags : [];
  const visibleSelectedTags = canShowTags ? selectedTags : [];

  const isMobile = deviceType === DeviceType.MOBILE;
  const isActive = isMobile && !!activeText;

  useEffect(() => {
    if (!pageOwner?.aui) return;
    void getTextTagList();
  }, [pageOwner?.aui]);

  const handleTags = () => {
    setStandardModal({
      modalType: ModalType.TEXT_TAG_STATION,
      title: 'Labels',
      value: '',
      handleChange: () => {},
    });
  };

  const handleCreateTag = () => {
    setStandardModal({
      modalType: ModalType.CREATE_TEXT_TAG,
      title: 'Create Tag',
      value: '',
      handleChange: () => {},
    });
  };

  const handleSelectTag = (tag: TextTagData) => {
    if (isEditMode) {
      requestExitEditMode({ confirmWhenClean: true });
      return;
    }
    if (selectedTags.some((t) => t.id === tag.id)) {
      removeSelectedTag(tag);
    } else {
      addSelectedTag(tag);
    }
  };

  const handleSelectUntagged = () => {
    if (isEditMode) {
      requestExitEditMode({ confirmWhenClean: true });
      return;
    }
    if (untaggedSelected) {
      clearUntagged();
    } else {
      selectUntagged();
    }
  };

  const noLabel = getModalMessage('NO_LABEL');

  const handleSelectTagModal = () => {
    setStandardModal({
      modalType: ModalType.SELECT_TEXT_TAG_MOBILE,
      title: 'Labels',
      value: '',
      handleChange: () => {},
    });
  };

  const hasTags = visibleTags.length > 0;

  return (
    <TextTagStationComp $isActive={isActive}>
      {!isEditMode && hasTags && isMobile && (
        <MobileLabelBtn onClick={handleSelectTagModal}>
          {isOwner && untaggedSelected ? (
            <>
              <LabelBlock>Label:</LabelBlock>
              <EmptyLabelMark $afterLabel>{getModalMessage('NO_LABEL_OPTION')}</EmptyLabelMark>
            </>
          ) : (
            <>
              <LabelBlock>
                Label :
              </LabelBlock>
              {visibleSelectedTags.length > 0 ? (
                <SelectedTagCount> · {getModalMessage('TAG_SELECTED_COUNT', { count: visibleSelectedTags.length })}</SelectedTagCount>
              ) : null}
            </>
          )}
        </MobileLabelBtn>
      )}
      {isEditMode && (
        <BtnContainer>
          <HintSmallWrapper content={getHintMessage('CHANGE_TAGS')}>
            <UniversalIconBtn
              size="fit"
              border={false}
              isTransparent
              backgroundImg={editIcon}
              onClick={handleTags}
            />
          </HintSmallWrapper>
          <HintSmallWrapper content={getHintMessage('ADD_TAG')}>
            <UniversalIconBtn
              size="small"
              border={false}
              isTransparent
              backgroundImg={createIcon}
              onClick={handleCreateTag}
            />
          </HintSmallWrapper>
        </BtnContainer>
      )}
      {hasTags && !isMobile && (
      <TextTagFactorContainer>
        {visibleTags.map((tag) => (
          <TextTagFactor
          key={tag.id}
          title={tag.name}
          $isSelected={visibleSelectedTags.some((t) => t.id === tag.id)}
          onClick={() => handleSelectTag(tag)}
          >
            {tag.name}
          </TextTagFactor>
        ))}
        {isOwner && (
          <TextTagFactor
            title={noLabel}
            $isSelected={untaggedSelected}
            onClick={handleSelectUntagged}
          >
            <EmptyLabelMark>{noLabel}</EmptyLabelMark>
          </TextTagFactor>
        )}
      </TextTagFactorContainer>
      )}
    </TextTagStationComp>
  );
};

const TextTagStationComp = styled.section<{ $isActive: boolean }>`
  width: ${({ $isActive }) => ($isActive ? '0%' : 'fit-content')};
  opacity: ${({ $isActive }) => ($isActive ? 0 : 1)};
  height: ${({ $isActive }) => ($isActive ? '0%' : 'calc(2vh);')};

  display: flex;
  justify-content: flex-start;
  align-items: center;

  gap: 12px;
  transition: all 0.7s ease-in-out;
  z-index: 3;
`;

const TextTagFactorContainer = styled.div`
  width: fit-content;
  max-width: 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 12px;
`;

const LabelBlock = styled.div`
  width: fit-content;
  height: 12px;
  display: flex;
  align-items: center;
`;

const MobileLabelBtn = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
  user-select: none;
`;

const SelectedTagCount = styled.span`
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Footer_01};
  font-style: italic;
  user-select: none;
`;

const TextTagFactor = styled.span <{ $isSelected: boolean }>`
  width: fit-content;
  flex-shrink: 0;
  border-bottom: 1px dotted ${({ $isSelected, theme }) => $isSelected ? theme.colors.color_Gray_03 : "transparent"};
  color: ${({ $isSelected, theme }) => $isSelected ? theme.colors.color_Gray_02 : theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
  user-select: none;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
`;

const BtnContainer = styled.div`
  width: fit-content;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const EmptyLabelMark = styled.span<{ $afterLabel?: boolean }>`
  display: inline-block;
  font-size: 0.8em;
  line-height: 1.5;
  vertical-align: 0.1em;
  margin-left: ${({ $afterLabel }) => ($afterLabel ? '4px' : '0')};
`;

export default TextTagStation;
